import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  ArrowLeft, Save, UserPlus, X, Check, Users, Search, Tag as TagIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { AthleteIdentityHealthCard } from '../components/athletes/AthleteIdentityHealthCard';

const API = process.env.REACT_APP_BACKEND_URL;

const POSITIONS = [
  'Goalkeeper', 'Right Back', 'Center Back', 'Left Back', 'Defender',
  'Defensive Midfielder', 'Central Midfielder', 'Midfielder',
  'Attacking Midfielder', 'Right Winger', 'Left Winger', 'Forward', 'Striker'
];
const SQUADS = ['U15', 'U17', 'U19', 'U21', 'Senior', 'Reserve'];
const FEET = ['Right', 'Left', 'Both'];
const GENDER_OPTIONS = ['Male', 'Female', 'Other', 'Prefer not to say'];

const AthleteManagement = () => {
  const { athleteId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const isAdmin = user?.role === 'Admin';

  const [athlete, setAthlete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  // Staff assignment state
  const [allStaff, setAllStaff] = useState([]);
  const [assignedStaffIds, setAssignedStaffIds] = useState([]);
  const [staffSearch, setStaffSearch] = useState('');
  const [savingAssignments, setSavingAssignments] = useState(false);

  // Tag assignment state
  const [allTags, setAllTags] = useState([]);
  const [assignedTagIds, setAssignedTagIds] = useState([]);
  const [savingTags, setSavingTags] = useState(false);

  // Phase 4 — Club affiliation state.
  const [clubs, setClubs] = useState([]);

  const fetchAthlete = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/athletes/${athleteId}`, { headers });
      const a = res.data;
      setAthlete(a);
      setForm({
        full_name: a.full_name || '',
        date_of_birth: a.date_of_birth || '',
        nationality: a.nationality || '',
        position: a.position || '',
        dominant_foot: a.dominant_foot || '',
        squad: a.squad || '',
        email: a.email || '',
        phone: a.phone || '',
        status: a.status || 'active',
        gender: a.gender || '',
        primary_club_id: a.primary_club_id || '',
        primary_age_group: a.primary_age_group || '',
        additional_age_groups: a.additional_age_groups || [],
      });
      setAssignedStaffIds(a.assigned_staff || []);
    } catch (err) {
      toast.error('Failed to load athlete');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }, [athleteId]);

  const fetchStaff = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/athletes/staff-list`, { headers });
      setAllStaff(res.data || []);
    } catch {
      // Staff list not critical for non-admin
    }
  }, []);

  const fetchTags = useCallback(async () => {
    try {
      const [tagsRes, athTagsRes] = await Promise.all([
        axios.get(`${API}/api/tags`, { headers }),
        axios.get(`${API}/api/tags/athlete/${athleteId}`, { headers }),
      ]);
      setAllTags(tagsRes.data || []);
      setAssignedTagIds((athTagsRes.data || []).map(t => t.id));
    } catch {
      /* silent */
    }
  }, [athleteId]);

  useEffect(() => {
    fetchAthlete();
    if (isAdmin) fetchStaff();
    fetchTags();
    // Load clubs for Phase 4 club affiliation selectors.
    axios.get(`${API}/api/clubs`, { headers })
      .then(r => setClubs(r.data || []))
      .catch(() => {});
  }, [fetchAthlete, fetchStaff, fetchTags, isAdmin]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.full_name?.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!form.date_of_birth) {
      toast.error('Date of birth is required');
      return;
    }

    setSaving(true);
    try {
      const payload = { ...form };
      // Only send changed fields
      const update = {};
      for (const key of Object.keys(payload)) {
        const currentVal = payload[key];
        const originalVal = athlete[key];
        // Array-aware diff for Phase 4 club fields.
        if (Array.isArray(currentVal) || Array.isArray(originalVal)) {
          const a = currentVal || [];
          const b = originalVal || [];
          if (a.length !== b.length || a.some((v, i) => v !== b[i])) {
            update[key] = a;
          }
        } else if (currentVal !== (originalVal || '')) {
          update[key] = currentVal || null;
        }
      }
      if (Object.keys(update).length === 0) {
        toast.info('No changes to save');
        setSaving(false);
        return;
      }
      await axios.put(`${API}/api/athletes/${athleteId}`, update, { headers });
      toast.success('Athlete updated successfully');
      await fetchAthlete();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAssignments = async () => {
    setSavingAssignments(true);
    try {
      await axios.put(`${API}/api/athletes/${athleteId}/assignments`, {
        staff_user_ids: assignedStaffIds
      }, { headers });
      toast.success('Staff assignments updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update assignments');
    } finally {
      setSavingAssignments(false);
    }
  };

  const toggleStaff = (staffId) => {
    setAssignedStaffIds(prev =>
      prev.includes(staffId)
        ? prev.filter(id => id !== staffId)
        : [...prev, staffId]
    );
  };

  const toggleTag = (tagId) => {
    setAssignedTagIds(prev =>
      prev.includes(tagId)
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };

  const handleSaveTags = async () => {
    setSavingTags(true);
    try {
      await axios.put(`${API}/api/tags/athlete/${athleteId}`, { tag_ids: assignedTagIds }, { headers });
      toast.success('Tags updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update tags');
    } finally {
      setSavingTags(false);
    }
  };

  const filteredStaff = allStaff.filter(s =>
    !staffSearch || s.full_name.toLowerCase().includes(staffSearch.toLowerCase()) ||
    s.email.toLowerCase().includes(staffSearch.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pfap-green" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl" data-testid="athlete-management-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-button">
            <ArrowLeft size={16} className="mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100" data-testid="page-title">
              Manage Athlete
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">{athlete?.full_name}</p>
          </div>
        </div>

        {/* Phase 85.1 — Athlete Identity Health + Recalculate widget */}
        <AthleteIdentityHealthCard
          athleteId={athleteId}
          canRecalculate={true}
        />

        {/* Edit Information Card */}
        <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-testid="edit-athlete-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-gray-900 dark:text-gray-100">Athlete Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-700 dark:text-gray-300">Full Name *</Label>
                <Input
                  value={form.full_name}
                  onChange={e => handleChange('full_name', e.target.value)}
                  placeholder="Full Name"
                  data-testid="input-full-name"
                />
              </div>
              <div>
                <Label className="text-gray-700 dark:text-gray-300">Date of Birth</Label>
                <Input
                  type="date"
                  value={form.date_of_birth || ''}
                  onChange={e => handleChange('date_of_birth', e.target.value)}
                  data-testid="input-dob"
                />
                <p className="text-[10px] text-gray-500 mt-1">
                  Leave blank if unknown — never invent a birthday.
                </p>
              </div>
              <div>
                <Label className="text-gray-700 dark:text-gray-300">DOB Status</Label>
                <select
                  value={form.dob_status || 'unknown'}
                  onChange={e => handleChange('dob_status', e.target.value)}
                  className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                  data-testid="select-dob-status"
                >
                  <option value="confirmed">Confirmed</option>
                  <option value="estimated">Estimated</option>
                  <option value="unknown">Unknown</option>
                </select>
                <p className="text-[10px] text-gray-500 mt-1">
                  Age-based benchmarks are marked provisional unless status is Confirmed.
                </p>
              </div>
              <div>
                <Label className="text-gray-700 dark:text-gray-300">Nationality</Label>
                <Input
                  value={form.nationality}
                  onChange={e => handleChange('nationality', e.target.value)}
                  placeholder="Nationality"
                  data-testid="input-nationality"
                />
              </div>
              <div>
                <Label className="text-gray-700 dark:text-gray-300">Position</Label>
                <select
                  value={form.position}
                  onChange={e => handleChange('position', e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100"
                  data-testid="select-position"
                >
                  <option value="">Select Position</option>
                  {POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-gray-700 dark:text-gray-300">Dominant Foot</Label>
                <select
                  value={form.dominant_foot}
                  onChange={e => handleChange('dominant_foot', e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100"
                  data-testid="select-foot"
                >
                  <option value="">Select Foot</option>
                  {FEET.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-gray-700 dark:text-gray-300">Gender</Label>
                <select
                  value={form.gender}
                  onChange={e => handleChange('gender', e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100"
                  data-testid="select-gender"
                >
                  <option value="">— Not set —</option>
                  {GENDER_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                  Used by Fitness Assessment standards. Leave blank if unknown.
                </p>
              </div>
              <div>
                <Label className="text-gray-700 dark:text-gray-300">Squad / Team</Label>
                <select
                  value={form.squad}
                  onChange={e => handleChange('squad', e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100"
                  data-testid="select-squad"
                >
                  <option value="">Select Squad</option>
                  {SQUADS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-gray-700 dark:text-gray-300">Email (optional)</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={e => handleChange('email', e.target.value)}
                  placeholder="athlete@email.com"
                  data-testid="input-email"
                />
              </div>
              <div>
                <Label className="text-gray-700 dark:text-gray-300">Phone (optional)</Label>
                <Input
                  value={form.phone}
                  onChange={e => handleChange('phone', e.target.value)}
                  placeholder="+351 000 000 000"
                  data-testid="input-phone"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-gray-700 dark:text-gray-300">Status</Label>
                <div className="flex items-center gap-2 mt-1" data-testid="status-toggle">
                  {[
                    { key: 'active', label: 'Active', hint: 'Appears in training, matches, attendance' },
                    { key: 'inactive', label: 'Inactive', hint: 'Hidden from operational lists; history preserved' },
                  ].map(opt => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => handleChange('status', opt.key)}
                      title={opt.hint}
                      className={`flex-1 px-3 py-2 rounded-md border text-sm font-medium transition-all ${
                        form.status === opt.key
                          ? opt.key === 'active'
                            ? 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-400 text-emerald-700 dark:text-emerald-300'
                            : 'bg-gray-100 dark:bg-gray-700 border-gray-400 text-gray-700 dark:text-gray-200'
                          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }`}
                      data-testid={`status-option-${opt.key}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                  Inactive athletes stay visible in the database, analytics, and history, but are hidden from training, matches, and attendance.
                </p>
              </div>
            </div>

            {/* Phase 4 — Club Affiliation */}
            <div className="mt-6 pt-5 border-t border-gray-200 dark:border-gray-700" data-testid="club-affiliation-section">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">
                Club Affiliation (Phase 4)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-gray-700 dark:text-gray-300">Primary Club</Label>
                  <select
                    value={form.primary_club_id || ''}
                    onChange={e => {
                      const v = e.target.value;
                      // Reset age groups when club changes (their options depend on club).
                      handleChange('primary_club_id', v);
                      handleChange('primary_age_group', '');
                      handleChange('additional_age_groups', []);
                    }}
                    className="w-full h-10 px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100"
                    data-testid="select-primary-club"
                  >
                    <option value="">— None (PFAP only) —</option>
                    {clubs.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name}{c.is_pfap ? ' (PFAP)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-gray-700 dark:text-gray-300">Primary Age Group</Label>
                  <select
                    value={form.primary_age_group || ''}
                    onChange={e => handleChange('primary_age_group', e.target.value)}
                    disabled={!form.primary_club_id}
                    className="w-full h-10 px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 disabled:opacity-50"
                    data-testid="select-primary-age-group"
                  >
                    <option value="">— Select age group —</option>
                    {(clubs.find(c => c.id === form.primary_club_id)?.age_groups || []).map(ag => (
                      <option key={ag} value={ag}>{ag}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-gray-700 dark:text-gray-300">Additional Age Groups</Label>
                  <div className="border border-gray-200 dark:border-gray-700 rounded-md p-2 min-h-[40px] flex flex-wrap gap-1.5 bg-white dark:bg-gray-800"
                       data-testid="additional-age-groups-pills">
                    {(clubs.find(c => c.id === form.primary_club_id)?.age_groups || [])
                      .filter(ag => ag !== form.primary_age_group)
                      .map(ag => {
                        const picked = (form.additional_age_groups || []).includes(ag);
                        return (
                          <button
                            key={ag}
                            type="button"
                            onClick={() => {
                              const cur = form.additional_age_groups || [];
                              handleChange(
                                'additional_age_groups',
                                picked ? cur.filter(x => x !== ag) : [...cur, ag]
                              );
                            }}
                            disabled={!form.primary_club_id}
                            className={`px-2 py-0.5 text-xs rounded border ${
                              picked
                                ? 'bg-emerald-600 text-white border-emerald-600'
                                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700'
                            }`}
                            data-testid={`additional-ag-${ag}`}
                          >
                            {ag}
                          </button>
                        );
                      })}
                    {!form.primary_club_id && (
                      <span className="text-xs text-gray-400">Pick a primary club first</span>
                    )}
                  </div>
                </div>
              </div>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-2">
                Used by the Match System to record external club matches. An athlete can sit in multiple age groups.
              </p>
            </div>

            <div className="flex justify-end pt-2">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-pfap-green hover:bg-pfap-green/90 text-white"
                data-testid="save-athlete-btn"
              >
                <Save size={16} className="mr-1.5" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Staff Assignment Card (Admin Only) */}
        {isAdmin && (
          <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-testid="staff-assignment-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg text-gray-900 dark:text-gray-100">
                  <Users size={18} className="inline mr-2 mb-0.5" />
                  Assigned Staff
                </CardTitle>
                <Badge className="bg-pfap-green/10 text-pfap-green border-pfap-green/20">
                  {assignedStaffIds.length} assigned
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Currently assigned */}
              <div className="flex flex-wrap gap-2" data-testid="assigned-staff-list">
                {assignedStaffIds.length === 0 && (
                  <p className="text-sm text-gray-400 dark:text-gray-500">No staff assigned yet</p>
                )}
                {assignedStaffIds.map(sid => {
                  const staff = allStaff.find(s => s.id === sid);
                  return (
                    <Badge
                      key={sid}
                      className="bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800 pl-2.5 pr-1 py-1 flex items-center gap-1.5"
                    >
                      {staff?.full_name || 'Unknown Staff'}
                      <button
                        onClick={() => toggleStaff(sid)}
                        className="hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full p-0.5"
                        data-testid={`remove-staff-${sid}`}
                      >
                        <X size={12} />
                      </button>
                    </Badge>
                  );
                })}
              </div>

              {/* Search + add staff */}
              <div>
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    placeholder="Search staff to assign..."
                    value={staffSearch}
                    onChange={e => setStaffSearch(e.target.value)}
                    className="pl-9 h-9"
                    data-testid="staff-search-input"
                  />
                </div>
                <div className="mt-2 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-700">
                  {filteredStaff.length === 0 && (
                    <p className="text-sm text-gray-400 dark:text-gray-500 p-3">No staff found</p>
                  )}
                  {filteredStaff.map(s => {
                    const isAssigned = assignedStaffIds.includes(s.id);
                    return (
                      <button
                        key={s.id}
                        onClick={() => toggleStaff(s.id)}
                        className={`w-full flex items-center justify-between px-3 py-2.5 text-left transition-colors ${
                          isAssigned
                            ? 'bg-pfap-green/5 dark:bg-pfap-green/10'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                        }`}
                        data-testid={`staff-option-${s.id}`}
                      >
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{s.full_name}</p>
                          <p className="text-xs text-gray-400 dark:text-gray-500">{s.email}</p>
                        </div>
                        {isAssigned ? (
                          <Check size={16} className="text-pfap-green flex-shrink-0" />
                        ) : (
                          <UserPlus size={14} className="text-gray-400 flex-shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  onClick={handleSaveAssignments}
                  disabled={savingAssignments}
                  className="bg-pfap-green hover:bg-pfap-green/90 text-white"
                  data-testid="save-assignments-btn"
                >
                  <Save size={16} className="mr-1.5" />
                  {savingAssignments ? 'Saving...' : 'Save Assignments'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tags Card (Admin + Staff) */}
        <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-testid="tags-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-gray-900 dark:text-gray-100">
                <TagIcon size={18} className="inline mr-2 mb-0.5" />
                Tags
              </CardTitle>
              <Badge className="bg-pfap-green/10 text-pfap-green border-pfap-green/20">
                {assignedTagIds.length} assigned
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {allTags.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No tags yet. <button onClick={() => navigate('/athlete-tags')} className="text-pfap-green hover:underline">Create one →</button>
              </p>
            ) : (
              <>
                <div className="flex flex-wrap gap-1.5" data-testid="tag-toggles">
                  {allTags.map(t => {
                    const isAssigned = assignedTagIds.includes(t.id);
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => toggleTag(t.id)}
                        className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-all ${
                          isAssigned
                            ? 'text-white shadow-sm'
                            : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                        }`}
                        style={isAssigned ? { background: t.color || '#10b981', borderColor: t.color || '#10b981' } : { borderColor: (t.color || '#64748b') + '66' }}
                        data-testid={`tag-toggle-${t.id}`}
                      >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: isAssigned ? '#fff' : (t.color || '#64748b') }} />
                        {t.name}
                      </button>
                    );
                  })}
                </div>
                <div className="flex justify-end pt-2">
                  <Button
                    onClick={handleSaveTags}
                    disabled={savingTags}
                    className="bg-pfap-green hover:bg-pfap-green/90 text-white"
                    data-testid="save-tags-btn"
                  >
                    <Save size={16} className="mr-1.5" />
                    {savingTags ? 'Saving…' : 'Save Tags'}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AthleteManagement;
